<script setup>
import { computed, ref } from 'vue';
import DOMPurify from 'isomorphic-dompurify';
import BaseBubble from './Base.vue';
import FileIcon from 'next/icon/FileIcon.vue';

const props = defineProps({
  id: { type: Number, required: true },
  content: { type: String, default: '' },
  contentAttributes: { type: Object, default: () => ({}) },
  conversationId: { type: Number, required: true },
});

const action = computed(() => props.contentAttributes?.action || {});
const parameters = computed(() => action.value?.parameters || {});

const headerConfig = computed(() => props.contentAttributes?.header || null);
const footerText = computed(() => props.contentAttributes?.footer || '');

const headerDocFileName = computed(() => {
  if (!headerConfig.value?.document) return '';
  return headerConfig.value.document.filename || 'Documento';
});

const headerDocFileType = computed(() => {
  return headerDocFileName.value.split('.').pop() || '';
});

const headerDocDisplayName = computed(() => {
  const name = headerDocFileName.value;
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) return name.length > 14 ? `${name.substring(0, 14)}...` : name;
  const base = name.substring(0, dotIndex);
  const ext = name.substring(dotIndex + 1);
  const truncated = base.length > 12 ? `${base.substring(0, 12).trimEnd()}..` : base;
  return `${truncated}.${ext}`;
});

const headerDocLink = computed(() => {
  return headerConfig.value?.document?.link || '';
});

// Helper para acessar chaves snake_case ou camelCase
const get = (obj, snake, camel) => {
  if (!obj) return undefined;
  return obj[snake] !== undefined ? obj[snake] : obj[camel];
};

const orderItems = computed(() => {
  const order = get(parameters.value, 'order', 'order');
  return order?.items || [];
});

const formatBRL = (amountObj) => {
  if (!amountObj) return 'R$ 0,00';
  const value = amountObj.value || 0;
  const offset = amountObj.offset || 100;
  const amount = value / offset;
  return `R$ ${amount.toFixed(2).replace('.', ',')}`;
};

const subtotal = computed(() => {
  const order = get(parameters.value, 'order', 'order');
  return formatBRL(order?.subtotal);
});

const tax = computed(() => {
  const order = get(parameters.value, 'order', 'order');
  const taxObj = order?.tax;
  if (!taxObj || taxObj.value === 0) return null;
  return {
    label: taxObj.description || 'Impostos',
    formatted: formatBRL(taxObj),
  };
});

const totalAmount = computed(() => {
  const amt = get(parameters.value, 'total_amount', 'totalAmount');
  return formatBRL(amt);
});

const referenceId = computed(() => {
  return get(parameters.value, 'reference_id', 'referenceId') || '';
});

const paymentSettings = computed(() => {
  return get(parameters.value, 'payment_settings', 'paymentSettings') || [];
});

const pixSetting = computed(() => {
  const pix = paymentSettings.value.find(
    s => s.type === 'pix_dynamic_code' || s.type === 'pixDynamicCode'
  );
  if (!pix) return null;
  return get(pix, 'pix_dynamic_code', 'pixDynamicCode') || null;
});

const boletoSetting = computed(() => {
  const boleto = paymentSettings.value.find(s => s.type === 'boleto');
  if (!boleto) return null;
  return boleto?.boleto || null;
});

// Valores extraidos com fallback snake/camel
const pixCode = computed(() => {
  if (!pixSetting.value) return null;
  return pixSetting.value.code || null;
});

const pixMerchantName = computed(() => {
  if (!pixSetting.value) return null;
  return get(pixSetting.value, 'merchant_name', 'merchantName') || null;
});

const pixKey = computed(() => {
  if (!pixSetting.value) return null;
  return pixSetting.value.key || null;
});

const pixKeyType = computed(() => {
  if (!pixSetting.value) return null;
  return get(pixSetting.value, 'key_type', 'keyType') || null;
});

const boletoDigitableLine = computed(() => {
  if (!boletoSetting.value) return null;
  return get(boletoSetting.value, 'digitable_line', 'digitableLine') || null;
});

const processMarkdown = (text) => {
  if (!text) return '';
  let processed = text;
  processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  processed = processed.replace(/\*(?!\s)([^*]+?)(?<!\s)\*/g, '<strong>$1</strong>');
  processed = processed.replace(/(?<![\w/])_([^_]+?)_(?![\w/])/g, '<em>$1</em>');
  processed = processed.replace(/\n/g, '<br>');
  return DOMPurify.sanitize(processed, {
    ALLOWED_TAGS: ['strong', 'em', 'br'],
    ALLOWED_ATTR: [],
  });
};

const processedContent = computed(() => processMarkdown(props.content));

// Estado de feedback ao copiar
const copiedField = ref(null);
let copiedTimeout = null;

const copyToClipboard = (text, field) => {
  navigator.clipboard.writeText(text);
  copiedField.value = field;
  if (copiedTimeout) clearTimeout(copiedTimeout);
  copiedTimeout = setTimeout(() => {
    copiedField.value = null;
  }, 2000);
};
</script>

<template>
  <BaseBubble class="px-4 py-3 max-w-sm" data-bubble-name="order-details">
    <div class="order-details-content">
      <!-- Header (document, image, text) -->
      <div v-if="headerConfig" class="mb-3">
        <div
          v-if="headerConfig.type === 'document' && headerConfig.document"
          class="h-9 bg-n-alpha-white gap-2 overflow-hidden items-center flex px-2 rounded-lg border border-n-container"
        >
          <FileIcon class="flex-shrink-0" :file-type="headerDocFileType" />
          <span
            class="flex-1 min-w-0 text-sm text-n-slate-12"
            :title="headerDocFileName"
          >
            {{ headerDocDisplayName }}
          </span>
          <a
            class="flex-shrink-0 size-9 grid place-content-center cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors ml-auto"
            :href="headerDocLink"
            rel="noreferrer noopener nofollow"
            target="_blank"
          >
            <span class="i-lucide-download" />
          </a>
        </div>
        <img
          v-else-if="headerConfig.type === 'image' && headerConfig.image"
          :src="headerConfig.image.link"
          class="rounded-lg w-full object-cover max-h-48"
        />
        <div
          v-else-if="headerConfig.type === 'text'"
          class="text-sm font-semibold"
        >
          {{ headerConfig.text }}
        </div>
      </div>

      <!-- Body text -->
      <div
        v-if="content"
        class="message-content mb-3 text-sm leading-relaxed"
        v-html="processedContent"
      />

      <!-- Referencia -->
      <div
        v-if="referenceId"
        class="text-xs text-n-slate-11 mb-3 truncate"
      >
        Ref: {{ referenceId }}
      </div>

      <!-- Itens do pedido -->
      <div v-if="orderItems.length" class="mb-3">
        <div class="text-xs font-semibold text-n-slate-11 mb-2 uppercase tracking-wide">
          Itens
        </div>
        <div class="space-y-2">
          <div
            v-for="(item, index) in orderItems"
            :key="index"
            class="flex items-center justify-between text-sm"
          >
            <div class="flex-1 min-w-0">
              <span class="truncate block">{{ item.name }}</span>
              <span
                v-if="item.quantity > 1"
                class="text-xs text-n-slate-11"
              >
                Qtd: {{ item.quantity }}
              </span>
            </div>
            <span class="font-medium ml-3 whitespace-nowrap">
              {{ formatBRL(item.amount) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Subtotal / Tax / Total -->
      <div class="border-t border-n-alpha-2 pt-2 mb-3 space-y-1">
        <div class="flex justify-between text-sm text-n-slate-11">
          <span>Subtotal</span>
          <span>{{ subtotal }}</span>
        </div>
        <div
          v-if="tax"
          class="flex justify-between text-sm text-n-slate-11"
        >
          <span>{{ tax.label }}</span>
          <span>{{ tax.formatted }}</span>
        </div>
        <div class="flex justify-between text-sm font-bold pt-1 border-t border-n-alpha-2">
          <span>Total</span>
          <span>{{ totalAmount }}</span>
        </div>
      </div>

      <!-- Botoes de pagamento -->
      <div class="space-y-2">
        <!-- Botao PIX -->
        <button
          v-if="pixSetting"
          class="copy-btn w-full rounded-xl p-3 bg-n-teal-3 border border-n-teal-6 text-left cursor-pointer hover:bg-n-teal-4 active:scale-[0.98] transition-all"
          @click="copyToClipboard(pixCode, 'pix')"
        >
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <div class="i-lucide-qr-code w-4 h-4 text-n-teal-11" />
              <span class="text-sm font-semibold text-n-teal-11">PIX Copia e Cola</span>
            </div>
            <div class="flex items-center gap-1 text-xs text-n-teal-11">
              <div
                :class="copiedField === 'pix' ? 'i-lucide-check' : 'i-lucide-copy'"
                class="w-3.5 h-3.5"
              />
              <span>{{ copiedField === 'pix' ? 'Copiado!' : 'Copiar' }}</span>
            </div>
          </div>
          <div class="text-xs text-n-slate-11 space-y-1 mb-2">
            <div v-if="pixMerchantName">
              {{ pixMerchantName }}
            </div>
            <div v-if="pixKey" class="flex items-center gap-1">
              <span class="text-n-slate-10">Chave ({{ pixKeyType }}):</span>
              <span class="font-mono">{{ pixKey }}</span>
            </div>
          </div>
          <div
            v-if="pixCode"
            class="p-2 rounded-lg bg-n-alpha-1 font-mono text-xs break-all select-all"
          >
            {{ pixCode }}
          </div>
        </button>

        <!-- Botao Boleto -->
        <button
          v-if="boletoSetting"
          class="copy-btn w-full rounded-xl p-3 bg-n-blue-3 border border-n-blue-6 text-left cursor-pointer hover:bg-n-blue-4 active:scale-[0.98] transition-all"
          @click="copyToClipboard(boletoDigitableLine, 'boleto')"
        >
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <div class="i-lucide-receipt w-4 h-4 text-n-blue-11" />
              <span class="text-sm font-semibold text-n-blue-11">Boleto</span>
            </div>
            <div class="flex items-center gap-1 text-xs text-n-blue-11">
              <div
                :class="copiedField === 'boleto' ? 'i-lucide-check' : 'i-lucide-copy'"
                class="w-3.5 h-3.5"
              />
              <span>{{ copiedField === 'boleto' ? 'Copiado!' : 'Copiar' }}</span>
            </div>
          </div>
          <div
            v-if="boletoDigitableLine"
            class="p-2 rounded-lg bg-n-alpha-1 font-mono text-xs break-all select-all"
          >
            {{ boletoDigitableLine }}
          </div>
        </button>
      </div>

      <!-- Footer -->
      <div v-if="footerText" class="mt-3 text-xs text-n-slate-10 italic">
        {{ footerText }}
      </div>
    </div>
  </BaseBubble>
</template>

<style scoped lang="scss">
.order-details-content {
  @apply flex flex-col;
}

.copy-btn {
  box-shadow:
    0 2px 4px -1px rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.06);

  &:active {
    box-shadow:
      inset 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  }
}
</style>
